import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Calendar, Clock, X, LogOut } from "lucide-react";
import AppointmentsSection from "./AppointmentSection";
import AvailabilitySection from "./AvailabilitySection";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import Dashboard from "./Dashboard";
import Header from "../Header";
import { collection, getDocs } from "firebase/firestore";
import { auth, db } from "../../firebase";

const navItems = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "appointments", label: "Appointments", icon: Calendar },
  { key: "availability", label: "Availability", icon: Clock },
];

export default function DoctorDashboard() {
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState({ name: "", role: "Doctor" });
  const [appointments, setAppointments] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isOpen, setIsOpen] = useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [quickConsultations, setQuickConsultations] = useState([]);
  const [callbackRequests, setCallbackRequests] = useState([]);

  // Fetch doctor info from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setDoctor({
        name: parsed.name || "Doctor",
        role: parsed.role || "Doctor",
      });
    }
  }, []);

  // Fetch all appointments
  const fetchAppointments = async () => {
    try {
      // ----------------------
      // Appointments
      // ----------------------
      const snapshot = await getDocs(collection(db, "appointments"));
      const appointmentsData = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          bookingId: data.bookingId || doc.id,
          name: data.name || "N/A",
          email: data.email || "N/A",
          phone: data.phone || "N/A",
          date: data.date || "N/A",
          time: data.time || "N/A",
          service: data.service || "N/A",
          status: data.status || "Pending",
          consultationType: data.consultationType || "Walk-in",
          branch: data.branch || "", // ✅ Added
          pincode: data.pincode || "", // ✅ Added
          locality: data.locality || "", // ✅ Added
          flatNo: data.flatNo || "", // ✅ Added
          landmark: data.landmark || "", // ✅ Added
          message: data.message || "N/A",
          cancelReason: data.cancelReason || "",
          createdAt: data.timestamp ? data.timestamp.toDate() : new Date(0),
        };
      });

      // Sort latest on top
      appointmentsData.sort((a, b) => b.createdAt - a.createdAt);
      setAppointments(appointmentsData);

      // ----------------------
      // Quick Consultations
      // ----------------------
      const quickSnap = await getDocs(collection(db, "quickConsultations"));
      const quickData = quickSnap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.timestamp ? data.timestamp.toDate() : new Date(0),
        };
      });
      quickData.sort((a, b) => b.createdAt - a.createdAt);
      setQuickConsultations(quickData);

      // ----------------------
      // Callback Requests
      // ----------------------
      const cbSnap = await getDocs(collection(db, "callbackRequests"));
      const cbData = cbSnap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.timestamp ? data.timestamp.toDate() : new Date(0),
        };
      });
      cbData.sort((a, b) => b.createdAt - a.createdAt);
      setCallbackRequests(cbData);
    } catch (err) {
      console.error("Error fetching appointments:", err);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  // Safe initials
  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "D";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <>
      {/* Header */}
      <Header hideBookButton={true} />

      {/* Sidebar */}
      <aside
        className={`fixed top-16 left-0 h-[calc(100%-4rem)] bg-white border-r shadow-sm flex flex-col justify-between transition-all duration-300 z-40
          ${isOpen ? "w-64" : "w-16"} md:${isOpen ? "w-64" : "w-20"}`}
      >
        {/* Top: Doctor Info / Toggle */}
        <div className="flex flex-col items-center py-2 border-b">
          {/* Toggle Button */}
          {isOpen && (
            <button
              onClick={() => setIsOpen(false)}
              className="self-end mr-2 text-blue-700 hover:text-blue-900"
            >
              <X size={20} />
            </button>
          )}

          {/* Only show avatar & name when sidebar is open */}
          {isOpen && (
            <div className="flex flex-col items-center mt-1">
              <div className="h-16 w-16 rounded-full bg-blue-200 text-blue-700 flex items-center justify-center font-bold text-xl mb-1 border-2 border-blue-600">
                {getInitials(doctor.name)}
              </div>
              <h2 className="text-lg font-semibold text-blue-700">
                {doctor.name}
              </h2>
              <p className="text-sm text-gray-500">{doctor.role}</p>
            </div>
          )}
          {/* Hamburger centered when collapsed */}
          {!isOpen && (
            <button
              onClick={() => setIsOpen(true)}
              className="flex items-center justify-center w-10 h-10 my-2 rounded hover:bg-gray-100"
            >
              <div className="flex flex-col justify-center items-center h-4 w-6 gap-1">
                <span className="block h-0.5 w-full bg-blue-700 rounded"></span>
                <span className="block h-0.5 w-full bg-blue-700 rounded"></span>
                <span className="block h-0.5 w-full bg-blue-700 rounded"></span>
              </div>
            </button>
          )}
        </div>

        {/* Navigation + Logout */}
        <nav className="flex-1 flex flex-col justify-between p-2 sm:p-3">
          <div className="flex flex-col gap-2">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={() => setActiveTab(item.key)}
                className={`flex items-center gap-3 w-full px-2 py-2 rounded-xl text-sm transition-colors
                  ${
                    activeTab === item.key
                      ? "bg-blue-100 text-blue-700"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
              >
                <item.icon size={20} />
                {isOpen && <span>{item.label}</span>}
              </button>
            ))}
          </div>

          {/* Logout */}
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="flex items-center gap-3 w-full px-2 py-2 rounded-xl text-sm text-red-600 hover:bg-red-100 mt-4"
          >
            <LogOut size={20} />
            {isOpen && <span>Logout</span>}
          </button>
        </nav>

        {/* Logout Dialog */}
        <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Logout</DialogTitle>
              <DialogDescription>
                Are you sure you want to log out?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-500 hover:bg-red-600 text-white"
                onClick={() => {
                  localStorage.removeItem("user");
                  auth.signOut();
                  navigate("/login");
                }}
              >
                Logout
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </aside>

      {/* Main Content */}
      <main
        className={`pt-24 px-2 sm:px-6 lg:px-8 transition-all
          ${isOpen ? "ml-16 sm:ml-64 md:ml-64" : "ml-16 sm:ml-20 md:ml-20"}`}
      >
        {activeTab === "dashboard" && <Dashboard appointments={appointments} />}

        {activeTab === "appointments" && (
          <AppointmentsSection
            appointments={appointments}
            quickConsultations={quickConsultations}
            callbackRequests={callbackRequests}
            fetchAppointments={fetchAppointments}
          />
        )}

        {activeTab === "availability" && <AvailabilitySection />}
      </main>
    </>
  );
}
